import { useEffect, useState } from "react";
import "./AdminSpecificUser.scss";
import {
  IActivity,
  IActivityObject,
  IBooking,
  ICoPlayerBooking,
  ICourt,
  INoShowReport,
  IRehabBooking,
  IUser,
  IUserHistory,
} from "../../types/api";
import { capitalizeWords } from "../../shared/utility";
import { combinedQuery } from "../../api/combinedQueries/combinedQueries";

import Spinner from "../../components/UI/Spinner/Spinner";
import { useNavigate, useParams } from "react-router-dom";
import { NoShowReportStatusEnum, StatusEnum } from "../../shared/enums";
import { toast } from "react-toastify";
import SlotHistoryPopup from "../../containers/SlotHistory/SlotHistoryPopup/SlotHistoryPopup";
import React from "react";
import EditNoShowReportPopup from "../../containers/NoShowReports/EditNoShowReportPopup/EditNoShowReportPopup";

interface IAdminSpecificUserProps {}

function AdminSpecificUser(props: IAdminSpecificUserProps) {
  const { memberId } = useParams<string>();
  const [userHistory, setUserHistory] = useState<IUserHistory[] | undefined>();

  const [selectedBookingForHistory, setSelectedBookingForHistory] = useState<
    IBooking | undefined
  >();

  const [
    selectedCoPlayerBookingForHistory,
    setSelectedCoPlayerBookingForHistory,
  ] = useState<ICoPlayerBooking | undefined>();
  const [
    selectedActivityBookingForHistory,
    setSelectedActivityBookingForHistory,
  ] = useState<IActivity | undefined>();

  const [showSlotHistoryPopupCoPlayer, setShowSlotHistoryPopupCoPlayer] =
    useState<boolean>(false);
  const [showSlotHistoryPopupActivity, setShowSlotHistoryPopupActivity] =
    useState<boolean>(false);

  const [showSlotHistoryPopup, setShowSlotHistoryPopup] =
    useState<boolean>(false);

  const [currentHistoryType, setCurrentHistoryType] = useState<
    | "bookings"
    | "activities"
    | "co-player-booking"
    | "user-history"
    | "rehab-history"
    | "no-show-reports"
  >("bookings");
  const navigate = useNavigate();
  const [user, setUser] = useState<IUser>();
  const [loading, setLoading] = useState<boolean>(false);
  const [bookings, setBookings] = useState<IBooking[]>([]);
  const [coPlayerBookings, setCoPlayerBookings] = useState<ICoPlayerBooking[]>(
    []
  );
  const [showEditNoShowReportPopup, setShowEditNoShowReportPopup] =
    useState<boolean>(false);

  const [noShowReportToEdit, setNoShowReportToEdit] = useState<INoShowReport>();
  const [activityBookings, setActivityBookings] = useState<IActivityObject[]>(
    []
  );
  const [rehabBookings, setRehabBookings] = useState<IRehabBooking[]>([]);
  const [activeRehabBookings, setActiveRehabBookings] = useState<
    IRehabBooking[]
  >([]);

  const [noShowReports, setNoShowReports] = useState<INoShowReport[]>([]);

  const [activeActivityBookings, setActiveActivityBookings] = useState<
    IActivityObject[]
  >([]);
  const [courts, setCourts] = useState<ICourt[]>([]);

  const openEditNoShow = (report: INoShowReport) => {
    setNoShowReportToEdit(report);
    setShowEditNoShowReportPopup(true);
  };
  const fetchActiveActivitiesAndRehabBookingsForUserView = async () => {
    setLoading(true);

    try {
      const response = await combinedQuery({
        requestor: "admin",
        member_id: memberId,
        active: true,
        fetchActiveActivities: true,
        fetchActiveRehabBookingsByMember: true,
      });

      if (
        response.activeActivities &&
        !("message" in response.activeActivities)
      ) {
        const activityBookings = response.activeActivities.sort(
          (a: IActivityObject, b: IActivityObject) =>
            new Date(b.activity.datetime_start).getTime() -
            new Date(a.activity.datetime_start).getTime()
        );
        setActiveActivityBookings(activityBookings);
      }
      if (
        response.activeRehabBookings &&
        !("message" in response.activeRehabBookings)
      ) {
        const rehabBookings = response.activeRehabBookings.sort(
          (a: IRehabBooking, b: IRehabBooking) =>
            new Date(b.datetime_start).getTime() -
            new Date(a.datetime_start).getTime()
        );
        setActiveRehabBookings(rehabBookings);
      }

      setLoading(false);
    } catch (error: any) {
      toast.error(error);
      setLoading(false);
    }
  };

  const fetchChosenItems = async ({
    fetchUserData = false,
    fetchBookingsByMember = false,
    fetchCoPlayerBookingsByMember = false,
    fetchCourts = false,
    fetchActiveActivities = false,
    fetchUserHistory = false,
    fetchActiveRehabBookingsByMember = false,
    fetchNoShowReportsByMember = false,
  }) => {
    setLoading(true);

    try {
      const response = await combinedQuery({
        requestor: "admin",
        member_id: memberId,
        fetchUserData: fetchUserData,
        active: false,
        fetchBookingsByMember: fetchBookingsByMember,
        fetchCoPlayerBookingsByMember: fetchCoPlayerBookingsByMember,
        fetchActiveActivities: fetchActiveActivities,
        fetchCourts: fetchCourts,
        fetchUserHistory: fetchUserHistory,
        fetchActiveRehabBookingsByMember: fetchActiveRehabBookingsByMember,
        fetchNoShowReportsByMember: fetchNoShowReportsByMember,
      });

      if (
        fetchUserData &&
        response.userData &&
        !("message" in response.userData)
      ) {
        setUser(response.userData);
      }
      const normalize = (d: string) =>
        d.replace(/(\+\d{2}:\d{2}):\d{2}$/, "$1");
      if (
        response.noShowReportsByMember &&
        !("message" in response.noShowReportsByMember)
      ) {
        const noShowReports = response.noShowReportsByMember.sort(
          (a: INoShowReport, b: INoShowReport) =>
            new Date(normalize(b.date)).getTime() -
            new Date(normalize(a.date)).getTime()
        );
        setNoShowReports(noShowReports);
      }
      if (
        fetchUserHistory &&
        response.userHistory &&
        !("message" in response.userHistory)
      ) {
        setUserHistory(response.userHistory);
      }
      if (
        fetchBookingsByMember &&
        response.bookingsByMember &&
        !("message" in response.bookingsByMember)
      ) {
        const bookings = response.bookingsByMember.sort(
          (a: IBooking, b: IBooking) =>
            new Date(b.datetime_start).getTime() -
            new Date(a.datetime_start).getTime()
        );

        setBookings(bookings);
      }
      if (
        fetchCoPlayerBookingsByMember &&
        response.coPlayerBookingsByMember &&
        !("message" in response.coPlayerBookingsByMember)
      ) {
        const bookings = response.coPlayerBookingsByMember.sort(
          (a: ICoPlayerBooking, b: ICoPlayerBooking) =>
            new Date(b.date).getTime() - new Date(a.date).getTime()
        );
        setCoPlayerBookings(bookings);
      }

      if (
        fetchActiveRehabBookingsByMember &&
        response.activeRehabBookings &&
        !("message" in response.activeRehabBookings)
      ) {
        const rehabBookings = response.activeRehabBookings.sort(
          (a: IRehabBooking, b: IRehabBooking) =>
            new Date(b.datetime_start).getTime() -
            new Date(a.datetime_start).getTime()
        );
        setRehabBookings(rehabBookings);
      }
      if (fetchCourts && response.courts && !("message" in response.courts)) {
        setCourts(response.courts);
      }

      if (
        fetchActiveActivities &&
        response.activeActivities &&
        !("message" in response.activeActivities)
      ) {
        const activityBookings = response.activeActivities.sort(
          (a: IActivityObject, b: IActivityObject) =>
            new Date(b.activity.datetime_start).getTime() -
            new Date(a.activity.datetime_start).getTime()
        );

        setActivityBookings(activityBookings);
      }

      setLoading(false);
    } catch (error: any) {
      toast.error(error);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchChosenItems({
      fetchUserData: true,
      fetchBookingsByMember: true,
      fetchCoPlayerBookingsByMember: true,
      fetchCourts: true,
      fetchActiveActivities: true,
      fetchUserHistory: true,
      fetchActiveRehabBookingsByMember: true,
      fetchNoShowReportsByMember: true,
    });
    fetchActiveActivitiesAndRehabBookingsForUserView();
  }, []);

  useEffect(() => {
    if (selectedBookingForHistory) {
      setShowSlotHistoryPopup(true);
      return;
    }
    if (
      selectedCoPlayerBookingForHistory &&
      selectedCoPlayerBookingForHistory.date
    ) {
      setShowSlotHistoryPopupCoPlayer(true);
      return;
    }
    if (selectedActivityBookingForHistory) {
      setShowSlotHistoryPopupActivity(true);
      return;
    }
  }, [
    selectedBookingForHistory,
    selectedCoPlayerBookingForHistory,
    selectedActivityBookingForHistory,
  ]);

  const handleToggleHistoryType = (
    type:
      | "bookings"
      | "activities"
      | "co-player-booking"
      | "user-history"
      | "rehab-history"
      | "no-show-reports"
  ) => {
    setCurrentHistoryType(type);
  };
  const getBookingStatusClass = (status: string) => {
    switch (status) {
      case StatusEnum.CANCELLED:
        return "cancelled";
      case StatusEnum.CHECKED_IN:
        return "checked-in-specific";
      case StatusEnum.PAID_NOT_CHECKED_IN:
        return "confirmed";
      case StatusEnum.CONFIRMED:
        return "confirmed";
      case StatusEnum.SEMI_NO_SHOW:
        return "semi-no-show";
      case StatusEnum.NO_SHOW:
        return "no-show";
      case StatusEnum.PAID_NO_SHOW:
        return "no-show";
      case StatusEnum.PAID:
        return "paid";
      default:
        return "";
    }
  };

  const getBookingStatusName = (status: string) => {
    switch (status) {
      case StatusEnum.CANCELLED:
        return "AVBOKAD";
      case StatusEnum.CHECKED_IN:
        return "INCHECKAD";
      case StatusEnum.PAID_NOT_CHECKED_IN:
        return "BETALD - EJ INCHECKAD";
      case StatusEnum.CONFIRMED:
        return "BEKRÄFTAD";
      case StatusEnum.SEMI_NO_SHOW:
        return "SEMI NO-SHOW";
      case StatusEnum.NO_SHOW:
        return "NO-SHOW";
      case StatusEnum.PAID_NO_SHOW:
        return "BETALD NO-SHOW";
      case StatusEnum.PAID:
        return "BETALD";
      default:
        return status.toUpperCase();
    }
  };

  const getNoShowReportStatusClass = (status: string) => {
    switch (status) {
      case NoShowReportStatusEnum.RESOLVED:
        return "cancelled";
      case NoShowReportStatusEnum.ACTIVE:
        return "no-show";
      case NoShowReportStatusEnum.REMOVED:
        return "removed";
      default:
        return "cancelled";
    }
  };

  const upcomingBookings = bookings.filter(
    (booking) => new Date(booking.datetime_start) >= new Date()
  );

  const pastBookings = bookings.filter(
    (booking) => new Date(booking.datetime_start) < new Date()
  );

  const upcomingCoPlayerBookings = coPlayerBookings.filter(
    (booking) => new Date(booking.date) >= new Date()
  );

  const pastCoPlayerBookings = coPlayerBookings.filter(
    (booking) => new Date(booking.date) < new Date()
  );

  const upcomingActivityBookings = activeActivityBookings;
  const pastActivityBookings = activityBookings.filter(
    (activity) => new Date(activity.activity.datetime_start) < new Date()
  );
  if (!user) return <Spinner />;
  return (
    <div className="admin-handle-specific-user-layout">
      <a className="back-button text-m" onClick={() => navigate("/medlemmar")}>
        Tillbaka
      </a>
      <h1 className="text-l primary-text ">
        Historik för{" "}
        {capitalizeWords(user.first_name) +
          " " +
          capitalizeWords(user.last_name)}
        {user.frozen && <span className="frozen"> - medlem fryst</span>}
      </h1>
      <div className="handle-specific-user-actions-wrapper">
        <div className="history-toggle-wrapper">
          <button
            className={`toggle-button text-m ${
              currentHistoryType === "bookings" ? "active" : ""
            }`}
            onClick={() => handleToggleHistoryType("bookings")}
          >
            Bokningar
          </button>

          <button
            className={`toggle-button text-m ${
              currentHistoryType === "activities" ? "active" : ""
            }`}
            onClick={() => handleToggleHistoryType("activities")}
          >
            Aktiviteter
          </button>

          <button
            className={`toggle-button text-m ${
              currentHistoryType === "co-player-booking" ? "active" : ""
            }`}
            onClick={() => handleToggleHistoryType("co-player-booking")}
          >
            Bokningar som medspelare
          </button>
          <button
            className={`toggle-button text-m ${
              currentHistoryType === "rehab-history" ? "active" : ""
            }`}
            onClick={() => handleToggleHistoryType("rehab-history")}
          >
            Rehab bokningar
          </button>
          <button
            className={`toggle-button text-m ${
              currentHistoryType === "user-history" ? "active" : ""
            }`}
            onClick={() => handleToggleHistoryType("user-history")}
          >
            Användarhistorik
          </button>
          <button
            className={`toggle-button text-m ${
              currentHistoryType === "no-show-reports" ? "active" : ""
            }`}
            onClick={() => handleToggleHistoryType("no-show-reports")}
          >
            No-shows
          </button>
        </div>
        <div className="users-add-wrapper"></div>
      </div>
      <table className="history-table">
        <thead>
          <tr>
            <th className="text-m secondary-text">Datum & tid</th>

            {currentHistoryType === "bookings" ? (
              <>
                <th className="text-m secondary-text">Bana</th>
                <th className="text-m secondary-text">Status på bokning</th>
                <th className="text-m secondary-text">Spelare</th>
                <th className="text-m secondary-text">Kommentar</th>
              </>
            ) : currentHistoryType === "co-player-booking" ? (
              <>
                <th className="text-m secondary-text">Bana</th>
                <th className="text-m secondary-text">Status på bokning</th>
                <th className="text-m secondary-text">Huvudbokare</th>
              </>
            ) : currentHistoryType === "activities" ? (
              <>
                <th className="text-m secondary-text">Bana</th>
                <th className="text-m secondary-text">Status på bokning</th>
                <th className="text-m secondary-text">Aktivitet</th>
                <th className="text-m secondary-text">Max antal spelare</th>
                <th className="text-m secondary-text">Färg</th>
              </>
            ) : currentHistoryType === "user-history" ? (
              <>
                <th className="text-m secondary-text">Händelse</th>
                <th className="text-m secondary-text">Utförd av</th>
              </>
            ) : currentHistoryType === "no-show-reports" ? (
              <>
                <th className="text-m secondary-text">Typ</th>
                <th className="text-m secondary-text short-cell">Bana</th>
                <th className="text-m secondary-text">Slot tid</th>
                <th className="text-m secondary-text">Status</th>
                <th className="text-m secondary-text">Kommentar</th>
              </>
            ) : (
              currentHistoryType === "rehab-history" && <></>
            )}
          </tr>
        </thead>
        <tbody>
          {/* Bokningar */}
          {currentHistoryType === "bookings" && (
            <>
              {/* Kommande bokningar */}
              <tr className="non-clickable">
                <td className="text-m upcoming-bookings-header">
                  Kommande bokningar
                </td>
                <td></td>
                <td></td>
                <td></td>
                <td></td>
              </tr>
              {upcomingBookings.length > 0 ? (
                upcomingBookings.map((booking, index) => {
                  const courtData = courts.find(
                    (court) => booking.court_id === court.court_id
                  );
                  const playerAmount =
                    booking.co_players.length +
                    booking.guests.length +
                    1 +
                    booking.coach_id.length;

                  return (
                    <tr
                      key={index}
                      className="text-m"
                      onClick={() => setSelectedBookingForHistory(booking)}
                    >
                      <td>
                        {booking.datetime_start.slice(0, 10)},{" "}
                        {booking.datetime_start.slice(11, 16)}
                      </td>
                      <td>{courtData?.name}</td>
                      <td className={getBookingStatusClass(booking.status)}>
                        {getBookingStatusName(booking.status)}
                      </td>
                      <td>{playerAmount}</td>
                      <td>{booking.comment}</td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td className="text-m no-bookings">-</td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                </tr>
              )}

              {/* Passerade bokningar */}
              <tr className="non-clickable">
                <td className="text-m past-bookings-header">
                  Passerade bokningar
                </td>
                <td></td>
                <td></td>
                <td></td>
                <td></td>
              </tr>
              {pastBookings.length > 0 ? (
                pastBookings.map((booking, index) => {
                  const courtData = courts.find(
                    (court) => booking.court_id === court.court_id
                  );
                  const playerAmount =
                    booking.co_players.length +
                    booking.guests.length +
                    1 +
                    booking.coach_id.length;

                  return (
                    <tr
                      key={index}
                      className="text-m"
                      onClick={() => setSelectedBookingForHistory(booking)}
                    >
                      <td>
                        {booking.datetime_start.slice(0, 10)},{" "}
                        {booking.datetime_start.slice(11, 16)}
                      </td>
                      <td>{courtData?.name}</td>
                      <td className={getBookingStatusClass(booking.status)}>
                        {getBookingStatusName(booking.status)}
                      </td>
                      <td>{playerAmount}</td>
                      <td>{booking.comment}</td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td className="text-m no-bookings">-</td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                </tr>
              )}
            </>
          )}

          {/* Bokningar som medspelare */}
          {currentHistoryType === "co-player-booking" && (
            <>
              {/* Kommande bokningar som medspelare */}
              <tr className="non-clickable">
                <td className="text-m upcoming-bookings-header">
                  Kommande bokningar som medspelare
                </td>
                <td></td>
                <td></td>
                <td></td>
              </tr>
              {upcomingCoPlayerBookings.length > 0 ? (
                upcomingCoPlayerBookings.map((booking, index) => {
                  const courtData = courts.find(
                    (court) => booking.court_id === court.court_id
                  );

                  return (
                    <tr
                      key={index}
                      className="text-m"
                      onClick={() =>
                        setSelectedCoPlayerBookingForHistory(booking)
                      }
                    >
                      <td>
                        {booking.date.slice(0, 10)},{" "}
                        {booking.date.slice(11, 16)}
                      </td>
                      <td>{courtData?.name}</td>
                      <td className={getBookingStatusClass(booking.status)}>
                        {getBookingStatusName(booking.status)}
                      </td>
                      <td>{capitalizeWords(booking.booker_name)}</td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td className="text-m no-bookings">-</td>
                  <td></td>
                  <td></td>
                  <td></td>
                </tr>
              )}

              {/* Passerade bokningar som medspelare */}
              <tr className="non-clickable">
                <td className="text-m past-bookings-header">
                  Passerade bokningar som medspelare
                </td>
                <td></td>
                <td></td>
                <td></td>
              </tr>
              {pastCoPlayerBookings.length > 0 ? (
                pastCoPlayerBookings.map((booking, index) => {
                  const courtData = courts.find(
                    (court) => booking.court_id === court.court_id
                  );

                  return (
                    <tr
                      key={index}
                      className="text-m"
                      onClick={() =>
                        setSelectedCoPlayerBookingForHistory(booking)
                      }
                    >
                      <td>
                        {booking.date.slice(0, 10)},{" "}
                        {booking.date.slice(11, 16)}
                      </td>
                      <td>{courtData?.name}</td>
                      <td className={getBookingStatusClass(booking.status)}>
                        {getBookingStatusName(booking.status)}
                      </td>
                      <td>{capitalizeWords(booking.booker_name)}</td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td className="text-m no-bookings">-</td>
                  <td></td>
                  <td></td>
                  <td></td>
                </tr>
              )}
            </>
          )}

          {/* Aktiviteter */}
          {currentHistoryType === "activities" && (
            <>
              {/* Kommande aktiviteter */}
              <tr className="non-clickable">
                <td className="text-m upcoming-bookings-header">
                  Kommande aktiviteter
                </td>
                <td></td>
                <td></td>
                <td></td>
                <td></td>
                <td></td>
              </tr>
              {upcomingActivityBookings.length > 0 ? (
                upcomingActivityBookings.map((activity, index) => {
                  const courtData = courts.find(
                    (court) => activity.activity.court_id[0] === court.court_id
                  );
                  return (
                    <tr
                      key={index}
                      className="text-m"
                      onClick={() =>
                        setSelectedActivityBookingForHistory(activity.activity)
                      }
                    >
                      <td>
                        {activity.activity.datetime_start.slice(0, 10)},{" "}
                        {activity.activity.datetime_start.slice(11, 16)}
                      </td>

                      <td>{courtData?.name}</td>
                      <td className={getBookingStatusClass(activity.status)}>
                        {getBookingStatusName(activity.status)}
                      </td>
                      <td>{activity.activity.name.split("%-%")[0]}</td>
                      <td>{activity.activity.participants_limit}</td>
                      <td>
                        <div
                          className={`activity-color-blob ${activity.activity.color}`}
                        ></div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td className="text-m no-bookings">-</td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                </tr>
              )}

              {/* Passerade aktiviteter */}
              <tr className="non-clickable">
                <td className="text-m past-bookings-header">
                  Passerade aktiviteter
                </td>
                <td></td>
                <td></td>
                <td></td>
                <td></td>
              </tr>
              {pastActivityBookings.length > 0 ? (
                pastActivityBookings.map((activity, index) => {
                  const courtData = courts.find(
                    (court) => activity.activity.court_id[0] === court.court_id
                  );
                  return (
                    <tr
                      key={index}
                      className="text-m"
                      onClick={() =>
                        setSelectedActivityBookingForHistory(activity.activity)
                      }
                    >
                      <td>
                        {activity.activity.datetime_start.slice(0, 10)},{" "}
                        {activity.activity.datetime_start.slice(11, 16)}
                      </td>
                      <td>{courtData?.name}</td>
                      <td className={getBookingStatusClass(activity.status)}>
                        {getBookingStatusName(activity.status)}
                      </td>
                      <td>{activity.activity.name.split("%-%")[0]}</td>
                      <td>{activity.activity.participants_limit}</td>
                      <td>
                        <div
                          className={`activity-color-blob ${activity.activity.color}`}
                        ></div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td className="text-m no-bookings">-</td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                </tr>
              )}
            </>
          )}

          {currentHistoryType === "user-history" && userHistory && (
            <>
              <tr className="non-clickable">
                <td className="text-m user-history-header">Användarhistorik</td>
                <td></td>
                <td></td>
              </tr>
              {userHistory?.length > 0 ? (
                userHistory.map((history, index) => (
                  <tr key={index} className="text-m">
                    <td>
                      {history.created_at.slice(0, 10)},{" "}
                      {history.created_at.slice(11, 16)}
                    </td>
                    <td>{history.event}</td>
                    <td>{history.created_by}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={3} className="text-m no-bookings">
                    Ingen användarhistorik
                  </td>
                </tr>
              )}
            </>
          )}
          {currentHistoryType === "rehab-history" && (
            <>
              {/* Upcoming rehab bookings */}
              <tr className="non-clickable">
                <td className="text-m rehab-history-header">
                  Kommande rehabbokningar
                </td>
              </tr>
              {activeRehabBookings?.length > 0 ? (
                activeRehabBookings.map((booking, index) => (
                  <tr key={index} className="text-m">
                    <td>
                      {booking.datetime_start.slice(0, 10)},{" "}
                      {booking.datetime_start.slice(11, 16)}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td className="text-m no-bookings">-</td>
                </tr>
              )}

              {/* Past rehab bookings */}
              <tr className="non-clickable">
                <td className="text-m past-bookings-header">
                  Passerade rehabbokningar
                </td>
              </tr>
              {rehabBookings?.length > 0 ? (
                rehabBookings.map((booking, index) => (
                  <tr key={index} className="text-m">
                    <td>
                      {booking.datetime_start.slice(0, 10)},{" "}
                      {booking.datetime_start.slice(11, 16)}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td className="text-m no-bookings">-</td>
                </tr>
              )}
            </>
          )}
          {currentHistoryType === "no-show-reports" && (
            <>
              {noShowReports.length > 0 ? (
                noShowReports.map((report, index) => {
                  const date = report.date?.slice(0, 10) || "";
                  const time = report.date?.slice(11, 16) || "";

                  const courtName = report.court_id
                    ? courts.find((c) => c.court_id === report.court_id)
                        ?.name || "-"
                    : "-";

                  const slotDate = report.slot_datetime_start
                    ? report.slot_datetime_start.slice(0, 10)
                    : null;
                  const slotTime = report.slot_datetime_start
                    ? report.slot_datetime_start.slice(11, 16)
                    : null;

                  const status = report.status || "-";
                  const resolvedReason = report.resolved_reason
                    ? `- ${report.resolved_reason}`
                    : "";
                  const type =
                    report.type === "ACTIVITY"
                      ? "Aktivitet"
                      : report.type === "BOOKING"
                      ? "Bokning"
                      : "-";
                  const rawComment = report.comment || "-";
                  const comment =
                    rawComment.length > 30
                      ? rawComment.slice(0, 30) + "…"
                      : rawComment;
                  return (
                    <tr key={index} className="text-m">
                      <td>
                        {date}, {time}
                      </td>
                      <td>{type}</td>
                      <td className="short-cell">{courtName}</td>
                      <td>
                        {slotTime && slotDate !== null
                          ? `${slotDate}, ${slotTime}`
                          : "-"}
                      </td>

                      <td className={getNoShowReportStatusClass(report.status)}>
                        {status === "ACTIVE"
                          ? "AKTIV"
                          : status === "REMOVED"
                          ? "BORTTAGEN"
                          : "INAKTIV"}{" "}
                        {(status === "RESOLVED" || status === "REMOVED") &&
                          resolvedReason}
                      </td>
                      <td>{comment}</td>
                      <td>
                        <div
                          className="edit-component"
                          onClick={() => openEditNoShow(report)}
                        >
                          <div className="edit-dot"></div>
                          <div className="edit-dot"></div>
                          <div className="edit-dot"></div>
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td className="text-m no-bookings">Inga no-shows</td>
                </tr>
              )}
            </>
          )}
        </tbody>
      </table>

      <SlotHistoryPopup
        showPopup={showSlotHistoryPopup}
        onClose={() => {
          setShowSlotHistoryPopup(false);
          setSelectedBookingForHistory(undefined);
        }}
        onConfirm={() => console.log("confirm")}
        court_id={selectedBookingForHistory?.court_id || ""}
        start_time={
          selectedBookingForHistory?.datetime_start.split("T")[1] || ""
        }
        filterDate={
          new Date(selectedBookingForHistory?.datetime_start || new Date())
        }
        courts={courts}
      />
      <SlotHistoryPopup
        showPopup={showSlotHistoryPopupCoPlayer}
        onClose={() => {
          setShowSlotHistoryPopupCoPlayer(false);
          setSelectedCoPlayerBookingForHistory(undefined);
        }}
        onConfirm={() => console.log("confirm")}
        court_id={selectedCoPlayerBookingForHistory?.court_id || ""}
        start_time={selectedCoPlayerBookingForHistory?.date.split("T")[1] || ""}
        filterDate={
          new Date(selectedCoPlayerBookingForHistory?.date || new Date())
        }
        courts={courts}
      />
      <SlotHistoryPopup
        showPopup={showSlotHistoryPopupActivity}
        onClose={() => {
          setShowSlotHistoryPopupActivity(false);
          setSelectedActivityBookingForHistory(undefined);
        }}
        onConfirm={() => console.log("confirm")}
        court_id={selectedActivityBookingForHistory?.court_id[0] || ""}
        start_time={
          selectedActivityBookingForHistory?.datetime_start.split("T")[1] || ""
        }
        filterDate={
          new Date(
            selectedActivityBookingForHistory?.datetime_start || new Date()
          )
        }
        courts={courts}
      />
      <EditNoShowReportPopup
        showPopup={showEditNoShowReportPopup}
        onClose={() => {
          setShowEditNoShowReportPopup(false);
          setNoShowReportToEdit(undefined);
        }}
        courts={courts}
        noShowReport={noShowReportToEdit!}
        onConfirm={() => {
          fetchChosenItems({
            fetchNoShowReportsByMember: true,
          });
        }}
      />
    </div>
  );
}

export default AdminSpecificUser;
